import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  getFirestore,
  collection,
  addDoc,
  getDoc, // ← add this
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
} from 'firebase/firestore';

// Initialize Firestore
const db = getFirestore();
const auth = getAuth();

const Calculator = () => {
  const [valueChains, setValueChains] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
 const [newValueChain, setNewValueChain] = useState({
  name: '',
  editors: '',
  progress: 0,
});

  const [editingValueChain, setEditingValueChain] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const navigate = useNavigate();
  const handleRowClick = (id, page) => {
    navigate(`/kibalo/${id}`);
  };
  
  
  // Fetch authenticated user details
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user.uid);
  
        try {
          const userDocRef = doc(db, `users/${user.uid}`);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            setCurrentUserName(userDoc.data().fullName);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setCurrentUser(null);
        setCurrentUserName('');
      }
  
      setIsLoadingUser(false);
    });
  
    return () => unsubscribeAuth();
  }, []);
  

  // Handle modal toggle
  const handleModalToggle = () => {
    setIsModalOpen(!isModalOpen);
    setEditingValueChain(null); // Reset editing state when toggling
  };

  // Handle input change for new value chain fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(name, value); // Log the input changes
    setNewValueChain({ ...newValueChain, [name]: value });
  };
  

  // Create a new value chain
  const handleSaveValueChain = async () => {
    if (!newValueChain.name || !newValueChain.editors) {
      alert("Please fill out all fields.");
      return;
    }

    try {
      if (editingValueChain) {
        // Update existing value chain
        const docRef = doc(db, `users/${currentUser}/kibalos/${editingValueChain.id}`);
        await updateDoc(docRef, {
          name: newValueChain.name.trim(),
          editors: newValueChain.editors,
          lastUpdated: new Date().toISOString(),
        });
        console.log('✅ Enterprise updated successfully');
      } else {
        // Create new value chain
        const valueChain = {
          name: newValueChain.name.trim(),
          editors: newValueChain.editors,
          createdBy: currentUserName || '',
          progress: 0,
          dateCreated: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
        };
        const userDocRef = doc(db, "users", currentUser);
        const valueChainsCollectionRef = collection(userDocRef, "kibalos");
        await addDoc(valueChainsCollectionRef, valueChain);
        console.log('✅ Enterprise created successfully');
      }

      setIsModalOpen(false);
      setNewValueChain({ name: '', editors: '', progress: 0 });
      setEditingValueChain(null);
    } catch (error) {
      console.error('❌ Error saving Enterprise:', error);
    }
  };
  
  
  
  // Handle edit action
  const handleEdit = (id) => {
    const valueChainToEdit = valueChains.find(chain => chain.id === id);
    if (!valueChainToEdit) return;
    setEditingValueChain(valueChainToEdit);
    setNewValueChain({ name: valueChainToEdit.name, editors: valueChainToEdit.editors });
    setIsModalOpen(true);
  };

  // Handle delete action
  const handleDelete = async (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete === null) return;
    
    const docRef = doc(db, `users/${currentUser}/kibalos`, itemToDelete);
    try {
      await deleteDoc(docRef);
      console.log('Document permanently deleted successfully');
    } catch (error) {
      console.error('Error deleting document: ', error);
    }

    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;
  
    const userDocRef = doc(db, "users", currentUser);
    const valueChainsCollectionRef = collection(userDocRef, "kibalos");
  
    const unsubscribe = onSnapshot(valueChainsCollectionRef, (snapshot) => {
      const chains = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setValueChains(chains);
    }, (error) => {
      console.error('Error fetching value chains:', error);
    });
  
    return () => unsubscribe();
  }, [currentUser]);

  const filteredValueChains = valueChains.filter(chain =>
    chain.name && 
    chain.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Top Bar */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">ENTERPRISE ANALYSIS</h1>
        <button
          onClick={handleModalToggle}
          className="flex items-center bg-blue-500 text-white px-4 py-2 rounded-md shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <FaPlus className="mr-2" /> Create New Enterprise
        </button>
      </div>
      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by Enterprise Name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="p-2 border border-gray-300 rounded w-full"
        />
      </div>

      {/* Table */}
      <div className="bg-white shadow-md rounded-lg">
        <table className="min-w-full border border-gray-300">
          <thead className="bg-blue-500 text-white">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium">SN</th>
              <th className="px-6 py-3 text-left text-sm font-medium">
              Enterprise Name
              </th>
              <th className="px-6 py-3 text-left text-sm font-medium">
                Editors
              </th>
              <th className="px-6 py-3 text-left text-sm font-medium">
                Date Created
              </th>
              <th className="px-6 py-3 text-left text-sm font-medium">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredValueChains.map((chain, index) => (
             <tr
             key={index}
             className="hover:bg-gray-100"
             onClick={(e) => {
               if (e.target.closest('.dropdown-menu') || e.target.closest('.actions-button')) return;
               handleRowClick(chain.id);
             }}
           >
                           <td className="px-6 py-4 text-sm text-gray-800 border border-gray-300">
                  {index + 1}
                </td>
                <td className="px-6 py-4 text-sm text-gray-800 border border-gray-300">
                  {chain.name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 border border-gray-300">
                  {chain.editors}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 border border-gray-300">
                  {new Date(chain.dateCreated).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 border border-gray-300">
                  <div className="relative inline-block text-left">
                 <button
  className="actions-button inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
  id={`menu-button-${index}`}
  aria-expanded={activeDropdown === index}
  aria-haspopup="true"
  onClick={(e) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === index ? null : index);
  }}
>
  Actions
</button>


                 {activeDropdown === index && (
  <div
    className="dropdown-menu origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
    role="menu"
    aria-orientation="vertical"
    aria-labelledby={`menu-button-${index}`}
  >

    <div className="py-1" role="none">
      <button
        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left"
        onClick={(e) => {
          e.stopPropagation(); // Prevents row click
          handleEdit(chain.id);
        }}
      >
        <FaEdit className="inline mr-2" /> Edit
      </button>
      <button
        className="block px-4 py-2 text-sm text-red-400 w-full text-left cursor-not-allowed"
        onClick={(e) => {
          e.stopPropagation(); // Prevents row click
        }}
        disabled
      >
        <FaTrash className="inline mr-2" /> Delete
      </button>
    </div>
  </div>
)}

  </div>
</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-1/3"> 
            <h2 className="text-xl font-bold mb-4">{editingValueChain ? 'Edit Enterprise' : 'Create Enterprise'}</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Kibalo Name
              </label>
              <input
  type="text"
  name="name"
  value={newValueChain.name}
  onChange={handleInputChange}
  className="mt-1 p-2 border border-gray-300 rounded w-full"
/>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Editors
              </label>
              <input
  type="text"
  name="editors"
  value={newValueChain.editors}
  onChange={handleInputChange}
  className="mt-1 p-2 border border-gray-300 rounded w-full"
/>
            </div>
            <div className="flex justify-end">
            <button 
              onClick={handleSaveValueChain}
              className={`px-4 py-2 rounded-md shadow text-white ${isLoadingUser ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'}`}
              disabled={isLoadingUser}
             > 
            {editingValueChain ? 'Save Changes' : 'Create'}
            </button>


              <button
                onClick={handleModalToggle}
                className="ml-2 bg-gray-300 text-gray-700 px-4 py-2 rounded-md shadow hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-1/3">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p>Are you sure you want to delete this enterprise?</p>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="ml-2 bg-gray-300 text-gray-700 px-4 py-2 rounded-md shadow hover:bg-gray-400"
              >
                Cancel
              </button>
              <button onClick={confirmDelete} className="bg-red-500 text-white px-4 py-2 rounded-md shadow hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calculator;
